import React from "react";
import { observer } from "mobx-react";
import { Redirect } from "react-router-dom";
import { Trans, useTranslation } from "@tecma/i18n";
import { Button } from "@tecma/ds/button";
import { Checkbox } from "@tecma/ds/checkbox";
import { CheckboxGroup } from "@tecma/ds/checkbox-group";
import { Input } from "@tecma/ds/input";
import { Radio } from "@tecma/ds/radio";
import { RadioGroup } from "@tecma/ds/radio-group";
import { Spinner } from "@tecma/ds/spinner";

import useActivateAccount from "hooks/useActivateAccount";
import { LoginWrapper } from "components/LoginWrapper";
import { getPasswordValidators } from "pages/utils";
import { getParamByName } from "utils/urlParamUtils";

const ActivateAccountContent = observer(({ onSuccess }) => {
  const { t } = useTranslation();
  const secretToken = getParamByName("token");

  const validators = getPasswordValidators(t);

  const handleNewPasswordChange = (e) => {
    handleOnChangeValue({ type: "newPassword", payload: e.target.value });
  };

  const {
    formValues,
    errorsState,
    formError,
    isLoading,
    isEnabledNewsletter,
    isDisabledConfirmAction,
    isDisabledPrivacy,
    privacyDocumentHref,
    onCompleteRegistration,
    handleOnChangeValue,
    handlePasswordValidationChange,
    validatingToken,
  } = useActivateAccount(onSuccess);

  if (!secretToken) {
    return <Redirect exact to='/login' />;
  }

  const newPasswordError =
    (errorsState.newPassword && t(errorsState.newPassword)) ||
    (formValues.newPassword &&
      !errorsState.passwordIsValid &&
      t("businessplatform.resetPassword.passwordMust"));

  return (
    <LoginWrapper className='activate-account' loading={validatingToken}>
      <LoginWrapper.Header>
        <span className='login-wrapper-header-title'>
          {t("businessplatform.completeRegistration")}
        </span>
      </LoginWrapper.Header>
      <LoginWrapper.Content showAlert={!!formError} alertProps={{ title: formError }}>
        <div className='login-wrapper-content-wrapper'>
          <Input
            className='login-wrapper-content-input'
            value={formValues.newPassword}
            helperText={newPasswordError}
            label={t("businessplatform.resetPassword.createPassword")}
            name='text-input-2'
            invalid={Boolean(newPasswordError)}
            onChange={handleNewPasswordChange}
            onValidationChange={handlePasswordValidationChange}
            validators={validators}
            variant='password'
            placeholder={t("businessplatform.resetPassword.placeholder.createPassword")}
          />
          <Input
            className='login-wrapper-content-input'
            name='text-input-3'
            label={t("businessplatform.resetPassword.repeatPassword")}
            value={formValues.confirmPassword}
            helperText={t(errorsState.confirmPassword)}
            invalid={Boolean(errorsState.confirmPassword)}
            onChange={(e) => {
              handleOnChangeValue({ type: "confirmPassword", payload: e.target.value });
            }}
            variant='password'
            placeholder={t("businessplatform.resetPassword.placeholder.repeatPassword")}
          />
          {isEnabledNewsletter && (
            <>
              <RadioGroup
                row
                error={errorsState.subscription}
                helperText={t(errorsState.subscription)}
                label={t("businessplatform.resetPassword.newsletter")}
                onChange={(_, value) =>
                  handleOnChangeValue({ type: "subscription", payload: value === "true" })
                }
              >
                <Radio
                  value='true'
                  size='small'
                  label={t("businessplatform.resetPassword.label.yes")}
                  checked={formValues.subscription === true}
                  color={errorsState.subscription ? "error" : "default"}
                />
                <Radio
                  value='false'
                  size='small'
                  label={t("businessplatform.resetPassword.label.no")}
                  checked={formValues.subscription === false}
                  color={errorsState.subscription ? "error" : "default"}
                />
              </RadioGroup>
              <CheckboxGroup
                error={errorsState.privacyPolicy}
                helpText={errorsState.privacyPolicy && t(errorsState.privacyPolicy)}
              >
                <Checkbox
                  checked={formValues.privacyPolicy}
                  size='small'
                  onChange={() => {
                    handleOnChangeValue({ type: "privacyPolicy" });
                  }}
                  color={errorsState.privacyPolicy ? "error" : "default"}
                  disabled={isDisabledPrivacy}
                  label={
                    <Trans
                      i18nKey='businessplatform.resetPassword.privacyPolicy'
                      components={{
                        a: (
                          <a
                            className='login-wrapper-content-link'
                            rel='noreferrer'
                            target='_blank'
                            href={privacyDocumentHref}
                          />
                        ),
                      }}
                    />
                  }
                />
              </CheckboxGroup>
            </>
          )}
        </div>
        <Button disabled={isDisabledConfirmAction} fluid onClick={onCompleteRegistration}>
          {isLoading && <Spinner />}
          {t("businessplatform.resetPassword.complete")}
        </Button>
      </LoginWrapper.Content>
    </LoginWrapper>
  );
});

export default ActivateAccountContent;
